<template>
	<div class="b-select" :class="classes" v-click-outside="handleClickOutside">
		<div
			class="b-select__display"
			ref="display"
			@click="handleDisplayClick"
			tabindex="0"
		>
			<div>
				{{ selectedOption ? selectedOption.label : placeholder }}
			</div>
			<BIcon :name="isMenuOpen ? 'CaretUp' : 'CaretDown'" size="16px" />
		</div>

		<div ref="menu" class="b-select__menu">
			<div
				v-for="option in options"
				:key="option.id"
				:value="option.id"
				class="b-select__menuItem"
				:class="{
					'is-selected': option.id === value,
				}"
				@click="(e) => handleMenuItemClick(e, option.id)"
				tabindex="0"
			>
				<slot name="option" :option="option">
					{{ $t ? $t(option.label) : option.label }}
					<!-- pretty coupled but uncoupling i believe complicates things -->
				</slot>
			</div>
		</div>

		<div class="b-select__helperText" v-if="_helperText">
			{{ _helperText }}
		</div>
	</div>
</template>

<script>
import { computePosition, flip } from "@floating-ui/dom";
import clickOutside from "@/directives/click-outside";

import BIcon from "@/components/base/BIcon";

export default {
	name: "b-select",
	directives: {
		clickOutside,
	},
	components: {
		BIcon,
	},
	props: {
		value: {
			type: [String, Number],
			default: null,
		},
		options: {
			type: Array,
			default: () => [], // [{ id: '1', label: '1' }]
		},
		state: {
			type: String,
			default: "default",
			validator: (value) =>
				["default", "success", "error", "warning"].includes(value),
		},
		helperText: {
			type: [String, Object],
			default: "",
		},
		placeholder: {
			type: String,
			default: "",
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		color: {
			type: String,
			default: "light",
			validator: (value) => ["light", "dark"].includes(value),
		},
		menuPlacement: {
			type: String,
			default: "bottom-start",
		},
	},
	data() {
		return {
			isMenuOpen: false,
			currentMenuPlacement: null,
			boundHandleViewportInteraction: null,
		};
	},
	computed: {
		classes() {
			return [
				`is-${this.state}`,
				`is-${this.currentMenuPlacement}`,
				`b-select--${this.color}`,
				{
					"is-open": this.isMenuOpen,
					"is-readonly": this.readonly,
					"is-disabled": this.disabled,
				},
			];
		},
		_helperText() {
			if (typeof this.helperText === "string") {
				return this.helperText;
			}

			return this.helperText[this.state];
		},
		listeners() {
			return {
				...this.$listeners,
				input: (event) => {
					this.$emit("input", event.target.value);
				},
			};
		},
		selectedOption() {
			return this.options.find(({ id }) => id === this.value);
		},
	},
	methods: {
		handleDisplayClick() {
			if (this.disabled || this.readonly) {
				return;
			}

			if (!this.isMenuOpen) {
				this.updateMenuPosition();
				this.isMenuOpen = true;
			} else {
				this.isMenuOpen = false;
			}
		},
		handleClickOutside() {
			this.isMenuOpen = false;
		},
		handleMenuItemClick(e, id) {
			this.$emit("input", id);
			this.isMenuOpen = false;
		},
		updateMenuPosition() {
			const display = this.$refs.display;
			const menu = this.$refs.menu;

			computePosition(display, menu, {
				placement: this.menuPlacement,
				middleware: [flip()],
			}).then(({ x, y, placement }) => {
				this.currentMenuPlacement = placement;

				Object.assign(menu.style, {
					left: `${x}px`,
					top: `${y}px`,
				});
			});
		},

		handleViewportInteraction() {
			if (this.isMenuOpen) {
				this.updateMenuPosition();
			}
		},
	},
	created() {
		this.boundHandleViewportInteraction = this.handleViewportInteraction.bind(
			this
		);

		window.addEventListener("resize", this.boundHandleViewportInteraction);
		window.addEventListener("scroll", this.boundHandleViewportInteraction);
	},
	destroyed() {
		window.removeEventListener("resize", this.boundHandleViewportInteraction);
		window.removeEventListener("scroll", this.boundHandleViewportInteraction);
	},
};
</script>

<style lang="scss">
.b-select {
	--border-radius: 8px;

	position: relative;

	&--light {
		--background-display: var(--color-neutral-0);
		--background-display-hover: var(--color-neutral-0);
		--background-display-active: var(--color-neutral-0);
		--color-display: var(--color-neutral-600);
		--color-display-hover: var(--color-neutral-900);
		--background-menu-item: var(--color-neutral-0);
		--background-menu-item-hover: var(--color-neutral-25);
		--background-menu-item-selected: var(--color-primary-50);
	}

	&--dark {
		--background-display: var(--color-neutral-700);
		--background-display-hover: var(--color-neutral-800);
		--background-display-active: var(--color-neutral-900);
		--color-display: var(--color-neutral-0);
		--color-display-hover: var(--color-neutral-0);
		--background-menu-item: var(--color-neutral-0);
		--background-menu-item-hover: var(--color-neutral-25);
		--background-menu-item-selected: var(--color-primary-50);
	}
}

.b-select__display {
	display: flex;
	align-items: center;
	justify-content: space-between;
	border-radius: var(--border-radius);
	gap: 1rem;
	border: 1px solid transparent;
	padding: 12px 16px;
	cursor: pointer;
	height: var(--control-height-m);

	background: var(--background-display);
	color: var(--color-display);
}

.b-select__menu {
	position: absolute;
	z-index: 100;
	width: 100%;
	visibility: hidden;
	opacity: 0;
}

.b-select__menuItem {
	padding: 13px 16px;
	cursor: pointer;
	background: var(--background-menu-item);

	&:hover {
		background-color: var(--background-menu-item-hover);
	}

	&.is-selected {
		background-color: var(--background-menu-item-selected);
	}
}

.b-select:has(:focus),
.b-select:hover {
	.b-select__display {
		background: var(--background-display-hover);
		color: var(--color-display-hover);
	}
}

// .b-select here for specifity
.b-select .b-select__display:active {
	background: var(--background-display-active);
}

.b-select {
	&.is-open {
		.b-select__menu {
			visibility: visible;
			opacity: 1;
		}

		&.is-top-start {
			.b-select__display {
				border-top-left-radius: 0;
				border-top-right-radius: 0;
				border-top-color: var(--color-neutral-900);
			}
		}

		&.is-bottom-start {
			.b-select__display {
				border-bottom-left-radius: 0;
				border-bottom-right-radius: 0;
				border-bottom-color: var(--color-neutral-900);
			}
		}
	}

	&.is-top-start {
		.b-select__menuItem:first-child {
			border-radius: var(--border-radius) var(--border-radius) 0 0;
		}
	}

	&.is-bottom-start {
		.b-select__menuItem:last-child {
			border-radius: 0 0 var(--border-radius) var(--border-radius);
		}
	}
}

.b-select__helperText {
	font-size: var(--font-size-s);
	color: var(--color-neutral-600);
	padding-left: 16px;
	margin-top: 4px;
}

.b-select.is-warning {
	.b-select__display {
		border-bottom-color: var(--color-warning-300);
	}
}

.b-select.is-error {
	.b-select__display {
		border-bottom-color: var(--color-error-300);
	}
}

.b-select:is(.is-readonly, .is-disabled) {
	.b-select__display {
		background-color: var(--color-neutral-75);
		color: var(--color-neutral-400);
	}
}
</style>
